/**
 * @module useRealtimeAlerts
 * @description
 * Hook for subscribing to real-time alert events via Supabase Realtime.
 * When new alerts are inserted into the `alerts` table, this hook:
 * 1. Triggers Toast notifications for critical/high severity alerts
 * 2. Invalidates TanStack Query caches for alerts and global overview
 * 3. Ensures the UI reflects new data immediately without page reload
 *
 * Part of Chunk 5: Realtime Alert System implementation.
 *
 * @example
 * // In a component that needs real-time alert updates:
 * function AlertsPage() {
 *   useRealtimeAlerts(); // Just call the hook - it handles subscriptions
 *   // ... rest of component using useAlerts(), useGlobalOverview(), etc.
 * }
 */

import { useEffect, useRef, useState, useCallback, useId } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/services/supabase';
import { useToast } from '@/contexts/ToastContext';
import { queryKeys } from '@/services/queryClient';
import type { RealtimeChannel, RealtimePostgresInsertPayload } from '@supabase/supabase-js';
import type { Database } from '@/services/database.types';

/**
 * Connection status for the realtime subscription
 */
export type RealtimeConnectionStatus = 'connected' | 'reconnecting' | 'disconnected';

/**
 * Return value from useRealtimeAlerts — exposes connection state for UI indicators
 */
export interface UseRealtimeAlertsResult {
  /** Current connection status */
  connectionStatus: RealtimeConnectionStatus;
  /** Timestamp (epoch ms) of the last received realtime event */
  lastEventAt: number | null;
}

/**
 * Alert row type from the database
 */
type AlertRow = Database['public']['Tables']['alerts']['Row'];

/**
 * Severity levels that trigger toast notifications
 */
const HIGH_PRIORITY_IMPACTS = ['Alto', 'high', 'critical'];

/**
 * Mapping of impact levels to user-friendly Spanish labels
 */
const IMPACT_LABELS: Record<string, string> = {
  'Alto': 'Alta',
  'high': 'Alta',
  'critical': 'Crítica',
  'Medio': 'Media',
  'medium': 'Media',
  'Bajo': 'Baja',
  'low': 'Baja',
};

/**
 * Get toast type based on impact level
 */
function getToastType(impact: string | null): 'error' | 'warning' | 'info' {
  if (impact === 'critical' || impact === 'Alto') {
    return 'error';
  }
  if (impact === 'high') {
    return 'warning';
  }
  return 'info';
}

/**
 * Helper to invalidate all alert-related TanStack Query caches
 * Extracted as DRY helper for INSERT/UPDATE/DELETE handlers
 */
function invalidateAlertCaches(
  queryClient: ReturnType<typeof import('@tanstack/react-query').useQueryClient>,
  tenantId: string,
  storeId: string | null | undefined
): void {
  const invalidationPromises = [
    // Invalidate all alerts queries
    queryClient.invalidateQueries({
      queryKey: ['alerts'],
      refetchType: 'active',
    }),
    // Invalidate global overview (alert counts, recent activity)
    queryClient.invalidateQueries({
      queryKey: queryKeys.globalOverview(),
      refetchType: 'active',
    }),
    // Invalidate KPIs if alert has a store_id (may affect open_alerts KPI)
    storeId
      ? queryClient.invalidateQueries({
          queryKey: queryKeys.kpis(tenantId, storeId),
          refetchType: 'active',
        })
      : Promise.resolve(),
    // Invalidate risk patterns for the affected store
    storeId
      ? queryClient.invalidateQueries({
          queryKey: queryKeys.riskPatterns(storeId),
          refetchType: 'active',
        })
      : Promise.resolve(),
  ];

  Promise.all(invalidationPromises)
    .then(() => {
      if (import.meta.env.DEV) console.log('[useRealtimeAlerts] Query caches invalidated successfully');
    })
    .catch((err) => {
      if (import.meta.env.DEV) console.error('[useRealtimeAlerts] Failed to invalidate queries:', err);
    });
}

/**
 * Hook that subscribes to real-time INSERT events on the alerts table.
 * Automatically triggers cache invalidation and toast notifications.
 *
 * Includes exponential backoff retry logic for connection stability.
 *
 * SECURITY: Requires tenantId parameter for tenant isolation.
 * The subscription filter ensures only alerts for the current tenant are received.
 *
 * @param tenantId - Required tenant ID for security filtering
 * @returns Connection status and last event timestamp for UI indicators
 */
export function useRealtimeAlerts(tenantId: string | undefined): UseRealtimeAlertsResult {
  const queryClient = useQueryClient();
  const { addToast } = useToast();
  // The hook is mounted by several pages at once; supabase.channel(name) returns the
  // EXISTING channel for a reused name and .on() then throws after subscribe(), so
  // each instance needs its own channel name.
  const instanceId = useId().replace(/[^a-zA-Z0-9]/g, '');
  const channelRef = useRef<RealtimeChannel | null>(null);
  const mountedRef = useRef(true);
  const retryCountRef = useRef(0);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const stabilityTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isConnectedRef = useRef(false);
  const intentionalCloseRef = useRef(false);

  // Exposed reactive state for UI consumers
  const [connectionStatus, setConnectionStatus] = useState<RealtimeConnectionStatus>('disconnected');
  const [lastEventAt, setLastEventAt] = useState<number | null>(null);

  const recordEvent = useCallback(() => {
    setLastEventAt(Date.now());
  }, []);

  useEffect(() => {
    // SECURITY: Don't subscribe without tenant context - prevents cross-tenant data leakage
    if (!tenantId) {
      if (import.meta.env.DEV) console.warn('[useRealtimeAlerts] No tenantId provided - subscription disabled for security');
      return;
    }

    mountedRef.current = true;
    retryCountRef.current = 0;
    isConnectedRef.current = false;
    intentionalCloseRef.current = false;
    // Include tenantId in channel name for tenant-scoped subscription
    const channelName = `realtime_alerts_${tenantId}_${instanceId}`;

    // Max retries and backoff configuration
    const MAX_RETRIES = 5;
    const BASE_DELAY_MS = 1000;
    const MAX_DELAY_MS = 30000;
    // Time connection must be stable before resetting retry count
    const STABILITY_THRESHOLD_MS = 5000;

    /**
     * Calculate exponential backoff delay with jitter
     */
    function getRetryDelay(attempt: number): number {
      const exponentialDelay = Math.min(BASE_DELAY_MS * Math.pow(2, attempt), MAX_DELAY_MS);
      // Add jitter (±20%) to prevent thundering herd
      const jitter = exponentialDelay * 0.2 * (Math.random() - 0.5);
      return Math.round(exponentialDelay + jitter);
    }

    /**
     * Setup the realtime subscription
     */
    function setupSubscription(): void {
      if (!mountedRef.current) return;

      // Clean up existing channel before creating new one
      if (channelRef.current) {
        try {
          supabase.removeChannel(channelRef.current);
        } catch {
          // Ignore cleanup errors
        }
        channelRef.current = null;
      }

      try {
        // Create Supabase Realtime channel with tenant-scoped filter
        // SECURITY: Filter by tenant_id ensures only current tenant's alerts are received
        channelRef.current = supabase
          .channel(channelName)
          .on<AlertRow>(
            'postgres_changes',
            {
              event: 'INSERT',
              schema: 'public',
              table: 'alerts',
              filter: `tenant_id=eq.${tenantId}`,
            },
            (payload: RealtimePostgresInsertPayload<AlertRow>) => {
              // Only process if still mounted
              if (!mountedRef.current) return;

              const newAlert = payload.new;

              if (import.meta.env.DEV) {
                console.log('[useRealtimeAlerts] New alert received:', {
                  id: newAlert.id,
                  type: newAlert.type,
                  impact: newAlert.potential_impact,
                });
              }
              recordEvent();

              // Phase 2: Check severity and trigger toast for critical/high alerts
              const impact = newAlert.potential_impact;
              if (impact && HIGH_PRIORITY_IMPACTS.includes(impact)) {
                const impactLabel = IMPACT_LABELS[impact] || impact;
                const alertType = newAlert.type?.replace(/_/g, ' ') || 'Nueva alerta';
                const toastType = getToastType(impact);

                addToast(
                  `Alerta ${impactLabel}: ${alertType}`,
                  toastType
                );

                if (import.meta.env.DEV) console.log('[useRealtimeAlerts] Toast notification triggered for high-priority alert');
              }

              // Phase 3: Invalidate TanStack Query caches
              invalidateAlertCaches(queryClient, tenantId, newAlert.store_id);
            }
          )
          // P1-UX-4: Handle UPDATE events for status changes, assignments, etc.
          // SECURITY: Filter by tenant_id for tenant isolation
          .on<AlertRow>(
            'postgres_changes',
            {
              event: 'UPDATE',
              schema: 'public',
              table: 'alerts',
              filter: `tenant_id=eq.${tenantId}`,
            },
            (payload) => {
              if (!mountedRef.current) return;

              const updatedAlert = payload.new;
              if (import.meta.env.DEV) {
                console.log('[useRealtimeAlerts] Alert updated:', {
                  id: updatedAlert.id,
                  status: updatedAlert.status,
                });
              }
              recordEvent();

              // Invalidate caches to reflect updated data
              invalidateAlertCaches(queryClient, tenantId, updatedAlert.store_id);
            }
          )
          // P1-UX-4: Handle DELETE events for alert removal
          // SECURITY: Filter by tenant_id for tenant isolation
          .on<AlertRow>(
            'postgres_changes',
            {
              event: 'DELETE',
              schema: 'public',
              table: 'alerts',
              filter: `tenant_id=eq.${tenantId}`,
            },
            (payload) => {
              if (!mountedRef.current) return;

              const deletedAlert = payload.old;
              if (import.meta.env.DEV) {
                console.log('[useRealtimeAlerts] Alert deleted:', {
                  id: deletedAlert?.id,
                });
              }
              recordEvent();

              // Invalidate caches to remove deleted alert from UI
              invalidateAlertCaches(queryClient, tenantId, deletedAlert?.store_id);
            }
          )
          .subscribe((status) => {
            // Don't process if component is unmounting
            if (!mountedRef.current) return;

            if (status === 'SUBSCRIBED') {
              if (import.meta.env.DEV) console.log(`[useRealtimeAlerts] Successfully subscribed to ${channelName}`);
              isConnectedRef.current = true;
              setConnectionStatus('connected');

              // Clear any existing stability timer
              if (stabilityTimerRef.current) {
                clearTimeout(stabilityTimerRef.current);
              }

              // Only reset retry count after connection is stable for 5 seconds
              // This prevents infinite reconnect loops when server immediately closes connections
              stabilityTimerRef.current = setTimeout(() => {
                if (mountedRef.current && isConnectedRef.current) {
                  if (import.meta.env.DEV) console.log('[useRealtimeAlerts] Connection stable - resetting retry count');
                  retryCountRef.current = 0;
                }
              }, STABILITY_THRESHOLD_MS);
            } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
              isConnectedRef.current = false;

              // Clear stability timer - connection failed
              if (stabilityTimerRef.current) {
                clearTimeout(stabilityTimerRef.current);
                stabilityTimerRef.current = null;
              }

              // Only log and retry if we haven't exceeded max retries
              if (retryCountRef.current < MAX_RETRIES) {
                const delay = getRetryDelay(retryCountRef.current);
                retryCountRef.current++;
                setConnectionStatus('reconnecting');

                if (import.meta.env.DEV) {
                  if (status === 'CHANNEL_ERROR') {
                    console.warn(`[useRealtimeAlerts] Connection error. Retrying in ${delay}ms (attempt ${retryCountRef.current}/${MAX_RETRIES})`);
                  } else {
                    console.warn(`[useRealtimeAlerts] Timeout. Retrying in ${delay}ms (attempt ${retryCountRef.current}/${MAX_RETRIES})`);
                  }
                }

                // Schedule retry with exponential backoff
                retryTimerRef.current = setTimeout(() => {
                  if (mountedRef.current) {
                    setupSubscription();
                  }
                }, delay);
              } else {
                if (import.meta.env.DEV) console.warn(`[useRealtimeAlerts] Max retries (${MAX_RETRIES}) exceeded. Alerts will load via fetch but won't auto-refresh.`);
                setConnectionStatus('disconnected');
              }
            } else if (status === 'CLOSED') {
              isConnectedRef.current = false;

              // Clear stability timer - connection wasn't stable
              if (stabilityTimerRef.current) {
                clearTimeout(stabilityTimerRef.current);
                stabilityTimerRef.current = null;
              }

              // Channel was closed - could be intentional cleanup or connection drop
              // Only retry if component is still mounted and we didn't initiate the close
              if (mountedRef.current && !intentionalCloseRef.current && retryCountRef.current < MAX_RETRIES) {
                const delay = getRetryDelay(retryCountRef.current);
                retryCountRef.current++;
                setConnectionStatus('reconnecting');
                if (import.meta.env.DEV) console.log(`[useRealtimeAlerts] Channel closed unexpectedly. Reconnecting in ${delay}ms (attempt ${retryCountRef.current}/${MAX_RETRIES})`);
                retryTimerRef.current = setTimeout(() => {
                  if (mountedRef.current && !intentionalCloseRef.current) {
                    setupSubscription();
                  }
                }, delay);
              } else if (retryCountRef.current >= MAX_RETRIES) {
                if (import.meta.env.DEV) console.warn(`[useRealtimeAlerts] Max retries (${MAX_RETRIES}) exceeded for CLOSED status. Realtime alerts disabled.`);
                setConnectionStatus('disconnected');
              }
            }
          });
      } catch (err) {
        // Catch any errors during subscription setup
        if (import.meta.env.DEV) console.warn('[useRealtimeAlerts] Failed to setup realtime subscription:', err);

        // Retry on setup failure
        if (retryCountRef.current < MAX_RETRIES) {
          const delay = getRetryDelay(retryCountRef.current);
          retryCountRef.current++;
          retryTimerRef.current = setTimeout(() => {
            if (mountedRef.current) {
              setupSubscription();
            }
          }, delay);
        }
      }
    }

    // Delay initial subscription setup to avoid rapid connect/disconnect on fast navigation
    const subscriptionTimer = setTimeout(() => {
      if (mountedRef.current) {
        setupSubscription();
      }
    }, 100);

    // Cleanup: unsubscribe on unmount
    return () => {
      mountedRef.current = false;
      intentionalCloseRef.current = true; // Mark as intentional close to prevent retry
      clearTimeout(subscriptionTimer);

      // Clear any pending retry timers
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }

      // Clear stability timer
      if (stabilityTimerRef.current) {
        clearTimeout(stabilityTimerRef.current);
        stabilityTimerRef.current = null;
      }

      if (channelRef.current) {
        // Use try-catch to prevent errors during cleanup
        try {
          supabase.removeChannel(channelRef.current);
        } catch {
          // Silently ignore cleanup errors
        }
        channelRef.current = null;
      }
    };
  }, [queryClient, addToast, tenantId, recordEvent, instanceId]);

  return { connectionStatus, lastEventAt };
}

export default useRealtimeAlerts;
